// Hook for managing push notifications

import { useState, useEffect, useCallback } from 'react';
import { pushNotificationService } from '../services/push-notifications';
import { adminApi } from '../services/api';

interface FirebasePublicConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
}

interface UsePushNotificationsReturn {
  isSupported: boolean;
  isEnabled: boolean;
  isLoading: boolean;
  permission: NotificationPermission | null;
  token: string | null;
  error: string | null;
  enablePushNotifications: () => Promise<boolean>;
  disablePushNotifications: () => Promise<void>;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const [isSupported] = useState(() => pushNotificationService.isSupported);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check initial permission state
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Enable push notifications
  const enablePushNotifications = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Get Firebase config from backend
      const configResponse = await adminApi.getFirebasePublicConfig();
      const config: FirebasePublicConfig = configResponse.data;

      if (!config.projectId || !config.apiKey) {
        setError('Firebase non configuré. Contactez l\'administrateur.');
        setIsLoading(false);
        return false;
      }

      // 2. Initialize push notification service
      const initialized = await pushNotificationService.initialize(
        {
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          projectId: config.projectId,
          storageBucket: config.storageBucket,
          messagingSenderId: config.messagingSenderId,
          appId: config.appId
        },
        config.vapidKey
      );

      if (!initialized) {
        setError('Impossible d\'initialiser les notifications push');
        setIsLoading(false);
        return false;
      }

      // 3. Request permission
      const perm = await pushNotificationService.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        setError('Permission de notification refusée');
        setIsLoading(false);
        return false;
      }

      // 4. Get FCM token
      const fcmToken = await pushNotificationService.getToken();
      if (!fcmToken) {
        setError('Impossible d\'obtenir le token de notification');
        setIsLoading(false);
        return false;
      }

      setToken(fcmToken);

      // 5. Register token with backend
      await adminApi.registerPushToken({ token: fcmToken });

      setIsEnabled(true);
      setIsLoading(false);

      // Store in localStorage
      localStorage.setItem('pushNotificationsEnabled', 'true');
      localStorage.setItem('fcmToken', fcmToken);

      return true;
    } catch (err: any) {
      console.error('Failed to enable push notifications:', err);
      setError(err.message || 'Erreur lors de l\'activation des notifications');
      setIsLoading(false);
      return false;
    }
  }, []);

  // Disable push notifications
  const disablePushNotifications = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      const storedToken = localStorage.getItem('fcmToken');
      if (storedToken) {
        // Unregister token from backend
        await adminApi.unregisterPushToken({ token: storedToken });
      }

      // Clear local state (don't unregister the SW — it's shared with PWA/cache)
      setIsEnabled(false);
      setToken(null);
      localStorage.removeItem('pushNotificationsEnabled');
      localStorage.removeItem('fcmToken');
    } catch (err: any) {
      console.error('Failed to disable push notifications:', err);
      setError(err.message || 'Erreur lors de la désactivation');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check if previously enabled and refresh token automatically
  useEffect(() => {
    const wasEnabled = localStorage.getItem('pushNotificationsEnabled') === 'true';

    if (wasEnabled && permission === 'granted') {
      // Auto-refresh token on app start
      const refreshToken = async () => {
        try {
          // Get Firebase config
          const configResponse = await adminApi.getFirebasePublicConfig();
          const config = configResponse.data;

          if (!config.projectId || !config.apiKey) {
            console.warn('Firebase not configured, skipping token refresh');
            return;
          }

          // Initialize if needed
          if (!pushNotificationService.isInitialized) {
            await pushNotificationService.initialize(
              {
                apiKey: config.apiKey,
                authDomain: config.authDomain,
                projectId: config.projectId,
                storageBucket: config.storageBucket,
                messagingSenderId: config.messagingSenderId,
                appId: config.appId
              },
              config.vapidKey
            );
          }

          // Get fresh token
          const freshToken = await pushNotificationService.getToken();
          if (freshToken) {
            const storedToken = localStorage.getItem('fcmToken');

            // Register if token changed or first time
            if (freshToken !== storedToken) {
              await adminApi.registerPushToken({ token: freshToken });
              localStorage.setItem('fcmToken', freshToken);
              console.log('Push token refreshed and registered');
            }

            setToken(freshToken);
            setIsEnabled(true);
          }
        } catch (err) {
          console.error('Failed to refresh push token:', err);
          // Keep old state if refresh fails
          const storedToken = localStorage.getItem('fcmToken');
          if (storedToken) {
            setToken(storedToken);
            setIsEnabled(true);
          }
        }
      };

      refreshToken();
    }
  }, [permission]);

  return {
    isSupported,
    isEnabled,
    isLoading,
    permission,
    token,
    error,
    enablePushNotifications,
    disablePushNotifications
  };
}

export default usePushNotifications;
